import React, { useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogActions,
  DialogContent,
  TextField,
} from '@material-ui/core';
import { get, isEmpty, isNil, uniqueId } from 'lodash';
import { IntlShape, injectIntl } from 'react-intl';
import styled from 'styled-components';
import { FormControl, InputLabel, Select, MenuItem } from '@material-ui/core';
import { translate } from 'containers/services/translate';
import messages from 'containers/RecapManagerPage/messages';
import {
  CodesConfig,
  Listings,
  SagisConfig,
} from 'containers/AppContainer/typings';
import { COLORS } from 'constants/design';
import * as utils from 'containers/RecapManagerPage/utils';
import {
  ASSIGNMENT_FIELDS,
  DEFAULT_LAWYER_CODE,
  ITEM_KEYS,
  SAGIS_KEYS,
} from 'containers/RecapManagerPage/constants';
import {
  Assignees,
  AssignmentClaim,
  KeycloakUser,
} from 'containers/RecapManagerPage/typings';
import { Claim } from 'containers/PreOpeningPage/typings';
import PrimaryButton from 'components/Buttons/PrimaryButton';
import SecondaryButton from 'components/Buttons/SecondaryButton';
import { extractIntervenantType } from 'containers/RecapPage/utils';
import { FEATURES } from 'features/features';
import * as featureService from 'features/fflip-helpers';
import { resolveAssignedManager } from 'containers/RecapManagerPage/utils';
import { DEFAULT_SUB_SERVICE_ID } from 'containers/LawyerLegalProceedingsPage/JudgementOnMerits/constants';
import { Autocomplete } from '@material-ui/lab';
import InfoIcon from 'images/info-icon.svg';
import { CLAIM_STATUS, PROCEDURE_STATUSES } from 'containers/data-structure';
import { CORRECTIONAL_PROCEDURE_ID } from 'containers/AppContainer/AppBar/constants';
import userPrivileges, {
  PRIVILEGES,
} from 'containers/services/security/user-privileges';
import { filterStackHolder } from 'services/utils';
import currentUserStorage from 'containers/services/security/current-user-storage';

const StyledDiv = styled.div`
  width: 576px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;

  .select {
    width: 90%;
    margin-bottom: 33px;
    font-size: 13px;
  }
`;
const StyledDialogActions = styled(DialogActions)`
  display: flex;
  padding: 20px 30px;
  justify-content: space-between;

  .moveButton {
    border: 1px solid ${COLORS.YELLOW};
    background-color: ${COLORS.YELLOW};
    color: ${COLORS.LIMED_SPURCE};
    &:hover {
      background-color: ${COLORS.YELLOW};
    }
  }

  .Mui-disabled {
    opacity: 0.6;
  }
`;

export interface AssignmentDialogProps {
  intl: IntlShape;
  open: boolean;
  onClose: Function;
  zonesCodes: CodesConfig[];
  subServices: SagisConfig[];
  services: SagisConfig[];
  stackholders: SagisConfig[];
  requestAssigment: Function;
  claim: Claim;
  stackholderTypes: SagisConfig[];
  requestCorporealLawyers: Function;
  corporealLawyers: KeycloakUser[];
  shouldAssignOnSagis: boolean;
  isManagerOpenedClaimsPage: boolean;
  closeAndRefresh?: Function;
  isManager?: boolean;
  listings: Listings;
  claimNumber: string;
  judiciaryProcessType?: string;
  isReassignmentManager?: boolean;
  judiciaryProcessId?: number;
  isReassignmentLawyer?: boolean;
  isOpeningManager?: boolean;
  judgementAdd?: any;
  judgementOnMerits?: any;
  judgementAppeal?: any;
  judgementAddAppeal?: any;
  civilProceeding?: any;
  requestManagerOpenedClaims?: Function;
  isClaim?: boolean;
  riskKey?: string;
}

const AssignmentDialog: React.FC<AssignmentDialogProps> = (
  props,
): JSX.Element => {
  const AssignmentWarning = styled.div`
    padding: 16px 16px 24px 16px;
    border-radius: 8px;
    background: var(--primary-100, #e6f1fa);
    margin-top: 10px;
    margin-bottom: 20px;
  `;
  const assignedManagerCode = resolveAssignedManager(
    get(props.claim, 'lossLocation', ''),
    get(props.claim, 'lossDate', ''),
  );
  const existingAssignedManagerCode = get(props.claim, 'assignedManagerCode')
    ? get(props.claim, 'assignedManagerCode')
    : get(
        get(props.claim, 'assignment', []).find((el: any) => !el.isDeleted),
        'assignedManagerCode',
        null,
      );
  const isCurrentJudiciaryProcessPending = get(
    props,
    'civilProceeding',
    [],
  ).find(
    (el: any) =>
      el.id === props.judiciaryProcessId &&
      el.procedureNatureId !== CORRECTIONAL_PROCEDURE_ID &&
      el.status === PROCEDURE_STATUSES.PENDING,
  );
  const assignedManagerDefaultSubService = get(
    get(props.listings, 'assignmentManager', []).find(
      (el: any) => el.id === assignedManagerCode,
    ),
    'subServiceCode',
    DEFAULT_SUB_SERVICE_ID,
  );

  const { onClose, open } = props;
  const inputLabel = React.useRef<HTMLLabelElement>(null);

  const [assignTo, setAssignTo] = React.useState({
    subService: get(
      props.claim,
      ['assignment', 'assignedSubService'],
      assignedManagerDefaultSubService,
    ),
    lawyer: get(
      props.claim,
      ['assignment', 'assignedLawyer'],
      assignedManagerDefaultSubService,
    ),
    assignedManagerCode: existingAssignedManagerCode
      ? existingAssignedManagerCode
      : assignedManagerCode,
  });

  const [assignedLawyers, setAssignedLawyers] = React.useState<any>([]);

  useEffect(() => {
    if (!props.isManagerOpenedClaimsPage && isEmpty(assignedLawyers)) {
      setAssignedLawyers(
        get(props.claim, 'assignment', [])
          .filter(
            (el: any) =>
              String(el.judiciaryProcessId) ===
                String(props.judiciaryProcessId) &&
              el.isDeleted === false &&
              el.assignedLawyer !== DEFAULT_LAWYER_CODE,
          )
          .filter((value: any) => value != undefined)
          .map((el: any) => {
            return stakeholders.find(
              (item: any) => item.ItemKey === el.assignedLawyer,
            );
          }),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.claim, props.judiciaryProcessId, isEmpty(assignedLawyers)]);

  const oldAssignedLawyers = get(props.claim, 'assignment', [])
    .filter(
      (el: any) =>
        el.judiciaryProcessId === props.judiciaryProcessId &&
        el.isDeleted === false,
    )
    .map((el: any) => el.assignedLawyer);

  const assignedManagerName = get(
    get(props.listings, 'assignmentManager', []).find(
      (el: any) => el.id === assignTo.assignedManagerCode,
    ),
    'name',
    '',
  );

  const [stakeholders, setStakeholders] = React.useState([] as SagisConfig[]);
  const [subServices, setSubServices] = React.useState([] as SagisConfig[]);

  React.useEffect(() => {
    props.requestCorporealLawyers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  React.useEffect(() => {
    const _stackholders = utils.getListBySagisKey(
      props.stackholderTypes,
      props.stackholders,
      ITEM_KEYS.LAWYER,
      SAGIS_KEYS.ITEM_TYPE as keyof SagisConfig,
    );

    const lawyers = utils.getBawabaLawyers(
      _stackholders,
      props.corporealLawyers,
    );
    setStakeholders(lawyers);
  }, [props.stackholderTypes, props.stackholders, props.corporealLawyers]);

  React.useEffect(() => {
    const imaginSubServices = utils.getListBySagisKey(
      props.services,
      props.subServices,
      ITEM_KEYS.SUB_SERVICE,
      SAGIS_KEYS.ITEM_GROUP as keyof SagisConfig,
    );

    const filteredSubServices = utils.getSubServices(
      imaginSubServices,
      props.zonesCodes,
    );

    setSubServices(filteredSubServices);
  }, [props.services, props.subServices, props.zonesCodes]);
  React.useEffect(() => {
    if (props.isManager) {
      setAssignTo({
        lawyer: '',
        subService: get(
          props.claim,
          'assignedSubService',
          assignedManagerDefaultSubService,
        ),
        assignedManagerCode: existingAssignedManagerCode
          ? existingAssignedManagerCode
          : assignedManagerCode,
      });
    } else {
      setAssignTo({
        lawyer: get(props.claim, ['assignment', 'assignedLawyer'], ''),
        subService: get(
          props.claim,
          ['assignment', 'assignedSubService'],
          assignedManagerDefaultSubService,
        ),
        assignedManagerCode: existingAssignedManagerCode
          ? existingAssignedManagerCode
          : assignedManagerCode,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.claim]);
  const handleClose = () => {
    setAssignedLawyers([]);
    onClose();
  };

  const handleChange = (
    event: React.ChangeEvent<{
      value: unknown;
      name?: string;
    }>,
  ) => {
    const { value, name } = event.target;
    setAssignTo(ele => ({ ...ele, [name as string]: value }));
  };

  const handleAssignedLawyers = (
    event: React.ChangeEvent<{}>,
    value: any[],
    reason: any,
  ) => {
    setAssignedLawyers(value);
  };

  const handleValidation = () => {
    const lawyer = props.stackholders.find(e => e.ItemKey === assignTo.lawyer);
    const service = lawyer?.ItemType;
    const assignmentMultiplePayload = utils.formatMultipleAssignmentsDetails(
      (props.claim as unknown) as AssignmentClaim,
      assignTo as Assignees,
      service as string,
      props.shouldAssignOnSagis,
      props.isManager,
      [], //feenoteAmounts
      lawyer?.ItemDescription,
      props.claimNumber,
      assignedManagerName,
      assignedLawyers.map((el: any) => el?.ItemKey),
      props.stackholders,
      assignedManagerCode,
      oldAssignedLawyers,
    );
    props.requestAssigment(
      {
        ...assignmentMultiplePayload,
        intervenantTypeCode: extractIntervenantType(
          props.listings,
          get(props.claim, 'intervenantTypeCode'),
        ),
        intervenantCode: get(props.claim, 'codeLawyer'),
        isDefaultAssignment: false,
        updateSagisIntervenantProvision: featureService.hasFeature(
          FEATURES.EVOLUTION_P3,
        )
          ? !get(props.claim, 'isBasicManagement', false)
          : true,
        isReassignmentManager: Boolean(props.isReassignmentManager),
        currentjudiciaryProcessId: props.judiciaryProcessId,
        isOpeningManager: props.isOpeningManager,
        isFirstAssignment: isEmpty(
          get(props.claim, 'assignment', []).filter(
            (el: any) =>
              !el.isDeleted &&
              el.assignedLawyer &&
              el.assignedLawyer !== DEFAULT_LAWYER_CODE,
          ),
        ),
        isTrackingActive: featureService.hasFeature(
          FEATURES.TRACKING_ACTIONS_USER,
        ),
        assignedBy: currentUserStorage?.get()?.username,
        riskKey: props.riskKey,
        claimId: !props.isClaim
          ? get(props.claim, 'claimId')
          : get(props.claim, 'id'),
        isLawyerInboxesActive: featureService.hasFeature(
          FEATURES.LAWYER_INBOXES,
        ),
      },
      onSuccessCallback,
    );
  };

  const onSuccessCallback = () => {
    if (
      userPrivileges.userHasPrivilege(PRIVILEGES.ISOPENINGMANAGER) &&
      userPrivileges.userHasPrivilege(PRIVILEGES.ISMANAGER) &&
      featureService.hasFeature(FEATURES.ASSIGNMENT_JURISDICTION_LAWYER)
    )
      props.requestManagerOpenedClaims &&
        props.requestManagerOpenedClaims(CLAIM_STATUS.VALIDATED, 0, 25);
    setAssignedLawyers([]);
    return props.onClose();
  };

  return (
    <Dialog
      onClose={handleClose}
      aria-labelledby="simple-dialog-title"
      open={open}
    >
      <DialogTitle id="simple-dialog-title">
        {translate({ ...messages.assignmentDialogTitle }, props)}
        {props.judiciaryProcessType}
      </DialogTitle>
      <DialogContent>
        <StyledDiv>
          {!isEmpty(get(props.claim, 'assignment', [])) &&
            !props.isReassignmentManager && (
              <AssignmentWarning>
                <img src={InfoIcon} alt="InfoIcon" />
                <span style={{ margin: '5px' }}>
                  {isCurrentJudiciaryProcessPending
                    ? translate(
                        {
                          ...messages.assignmentCivilJudiciaryProcessLawyerWarning,
                        },
                        props,
                      )
                    : translate({ ...messages.assignmentLawyerWarning }, props)}
                </span>
              </AssignmentWarning>
            )}

          {!featureService.hasFeature(FEATURES.ASSIGNMENT_MANAGER_TOWN) && (
            <FormControl className="select">
              <InputLabel
                ref={inputLabel}
                id="demo-simple-select-outlined-label"
              >
                {translate(
                  { ...messages.assignmentDialogAssignToSubService },
                  props,
                )}
              </InputLabel>
              <Select
                name={ASSIGNMENT_FIELDS.SUB_SERVICE}
                value={assignTo.subService}
                onChange={handleChange}
                labelWidth={60}
              >
                {!isEmpty(subServices) &&
                  subServices.map(val => (
                    <MenuItem key={uniqueId('sub_service')} value={val.ItemKey}>
                      {val.ItemDescription}
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>
          )}
          {featureService.hasFeature(FEATURES.ASSIGNMENT_MANAGER_TOWN) && (
            <FormControl className="select">
              <InputLabel
                ref={inputLabel}
                id="demo-simple-select-outlined-label"
              >
                {translate(
                  { ...messages.assignmentDialogAssignToSubService },
                  props,
                )}
              </InputLabel>
              <Select
                name={ASSIGNMENT_FIELDS.ASSIGNED_MANAGER}
                value={assignTo.assignedManagerCode}
                onChange={handleChange}
                labelWidth={60}
                readOnly={!props.isReassignmentManager}
              >
                {get(props.listings, 'assignmentManager', []).map(
                  (val: any) => (
                    <MenuItem key={uniqueId('assigned_manager')} value={val.id}>
                      {val.name}
                    </MenuItem>
                  ),
                )}
              </Select>
            </FormControl>
          )}

          {!props.isReassignmentManager ? (
            featureService.hasFeature(FEATURES.ASSIGNMENT_MULTIPLE_LAWYERS) ? (
              <FormControl className="select">
                <Autocomplete
                  className="lawyer-item"
                  multiple
                  id="assignedLawyers"
                  options={filterStackHolder(stakeholders)}
                  getOptionLabel={(option: any) =>
                    `${option?.ItemDescription ? option?.ItemDescription : ''}`
                  }
                  value={assignedLawyers}
                  onChange={handleAssignedLawyers}
                  renderInput={params => (
                    <TextField {...params} variant="standard" />
                  )}
                />
              </FormControl>
            ) : (
              <FormControl className="select">
                <InputLabel
                  ref={inputLabel}
                  id="demo-simple-select-outlined-label"
                >
                  {translate(
                    { ...messages.assignmentDialogAssignLawyer },
                    props,
                  )}
                </InputLabel>
                <Select
                  name={ASSIGNMENT_FIELDS.LAWYER}
                  value={assignTo.lawyer}
                  onChange={handleChange}
                  labelWidth={60}
                >
                  {!isEmpty(stakeholders) &&
                    filterStackHolder(stakeholders).map((val: any) => (
                      <MenuItem
                        key={uniqueId('stackholder_')}
                        value={val.ItemKey}
                      >
                        {val.ItemDescription}
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>
            )
          ) : null}
        </StyledDiv>
      </DialogContent>
      <StyledDialogActions>
        <SecondaryButton
          onClick={handleClose}
          disableElevation
          title={translate({ ...messages.assignmentDialogCancelButton }, props)}
        />
        <PrimaryButton
          onClick={handleValidation}
          disableElevation
          disabled={
            isNil(assignTo.assignedManagerCode) ||
            isCurrentJudiciaryProcessPending
          }
          title={translate(
            { ...messages.assignmentDialogValidationButton },
            props,
          )}
        />
      </StyledDialogActions>
    </Dialog>
  );
};

AssignmentDialog.defaultProps = {
  open: false,
};

export default injectIntl(AssignmentDialog);
